/* Subscribe and save panel (RET-258, Phase 3 subscriptions lane).
   Markup: snippets/v2-subscription-panel.liquid. Contract with the product page:
   - listens to variant:change (bubbles from the product form; detail.variant);
   - owns the form's only input named selling_plan (disabled on one-time);
   - dispatches v2:subscription-change on document with
     { formId, sellingPlanId, price, compareAtPrice, label, rhythm } on every change and once on load.
   Seal safety net: while Seal's app still injects its widget, any Seal widget inside the product
   form (or its product-form element) is hidden, made inert and its inputs disabled, and the
   formdata handler keeps only the panel's selling_plan in every post. */

(() => {
  if (customElements.get('v2-subscription-panel')) return;

  // Seal's widget root and container (probe 2026-09-29): Seal renders
  // .sealsubs-target-element > .sealsubs-container[role=radiogroup] with its own inputs
  // (a hidden input, .sls-option radios and a .sls-select frequency select).
  const SEAL_SELECTOR = '.sealsubs-target-element, .sealsubs-container';
  const SEAL_DETAILS_LINK = '.sls-subscription-details-label a[href]';

  // Initial events wait for DOMContentLoaded, so listeners in scripts that load after this one
  // (deferred scripts run in document order) still get the load state.
  let domReady = document.readyState === 'complete';
  const pending = new Set();
  const flush = () => {
    domReady = true;
    for (const panel of pending) panel.emit({ initial: true });
    pending.clear();
  };
  if (!domReady) {
    document.addEventListener('DOMContentLoaded', flush, { once: true });
    window.addEventListener('load', flush, { once: true });
  }

  const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(value ?? ''), template || '');

  class V2SubscriptionPanel extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-v2-subs-data]');
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      this.plans = new Map(this.data.plans.map((plan) => [String(plan.id), plan]));
      this.formId = this.dataset.formId;
      this.form = this.closest('form') || document.getElementById(this.formId);
      this.scope = this.closest('product-form') || this.form || this;
      this.input = this.querySelector('[data-v2-selling-plan]');
      this.switch = this.querySelector('[data-subscription-switch]');
      this.body = this.querySelector('[data-v2-subs-body]');
      this.radios = [...this.querySelectorAll('[data-selling-plan-option]')];
      this.status = this.querySelector('[data-v2-subs-status]');
      this.detailsLink = this.querySelector('[data-v2-subs-details]');

      const idInput = this.form?.querySelector('input[name="id"]');
      this.variantId = idInput?.value ? String(idInput.value) : null;
      this.on = this.switch?.getAttribute('aria-checked') === 'true';

      this.abort?.abort();
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.switch?.addEventListener('click', () => this.toggle(), { signal });
      // The title and summary are part of the switch's label: a click there toggles too.
      this.querySelector('[data-v2-subs-head]')?.addEventListener('click', (event) => {
        if (!this.switch || this.switch.contains(event.target)) return;
        this.toggle();
      }, { signal });
      for (const radio of this.radios) {
        radio.addEventListener('change', () => this.update({ announce: true, userChange: true }), { signal });
      }
      document.addEventListener('variant:change', (event) => {
        if (!(event.target instanceof Node) || !event.target.contains(this)) return;
        const variant = event.detail?.variant;
        this.variantId = variant?.id ? String(variant.id) : null;
        this.update({ announce: false });
      }, { signal });

      if (this.form) {
        this.form.addEventListener('formdata', (event) => this.#cleanFormData(event.formData), { signal });
      }
      this.#watchSeal(signal);

      this.update({ announce: false, emit: false });
      if (domReady) this.emit({ initial: true });
      else pending.add(this);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.sealObserver?.disconnect();
      pending.delete(this);
    }

    get variant() {
      return this.variantId ? this.data.variants[this.variantId] || null : null;
    }

    get selectedRadio() {
      return this.radios.find((radio) => radio.checked && !radio.disabled) || null;
    }

    /* The detail the product page follows (price under the title, sticky bar). */
    get detail() {
      const variant = this.variant;
      const plan = this.on ? this.#currentPlan() : null;
      const allocation = plan && variant ? variant.plans[plan.id] : null;
      if (plan && allocation) {
        const [perDelivery, compareAt] = allocation;
        return {
          formId: this.formId,
          sellingPlanId: Number(plan.id),
          price: perDelivery,
          compareAtPrice: compareAt > perDelivery ? compareAt : null,
          label: fill(this.dataset.planLabel, { rhythm: plan.rhythm }),
          rhythm: plan.rhythm,
        };
      }
      return {
        formId: this.formId,
        sellingPlanId: null,
        price: variant ? variant.price : null,
        compareAtPrice: null,
        label: null,
        rhythm: null,
      };
    }

    toggle() {
      if (!this.switch || this.switch.getAttribute('aria-disabled') === 'true') return;
      this.on = !this.on;
      this.update({ announce: true, userChange: true });
    }

    update({ announce = false, emit = true, userChange = false } = {}) {
      const variant = this.variant;
      const required = Boolean(this.data.required);

      // Plans this variant can take; a plan the variant lacks is disabled.
      let available = 0;
      for (const radio of this.radios) {
        const ok = !variant || Boolean(variant.plans[radio.value]);
        radio.disabled = !ok;
        if (ok) available += 1;
      }
      if (!this.selectedRadio) {
        const first = this.radios.find((radio) => !radio.disabled);
        if (first) first.checked = true;
      }
      // A sold-out variant cannot be added: the panel hides (subscription-only products keep
      // it) and the switch comes back as it was when an in-stock variant is chosen.
      const soldOut = Boolean(variant) && variant.available === false && !required;
      if (soldOut && this.on) this.resumeOn = true;
      if (!soldOut && this.resumeOn) {
        this.on = true;
        this.resumeOn = false;
      }
      this.hidden = soldOut;
      const canSubscribe = !soldOut && (Boolean(variant) ? available > 0 : this.radios.length > 0);
      if (required && canSubscribe) this.on = true;
      if (!canSubscribe) this.on = false;

      const plan = this.#currentPlan();
      this.switch?.setAttribute('aria-checked', String(this.on));
      if (this.switch) {
        if (required || !canSubscribe) this.switch.setAttribute('aria-disabled', 'true');
        else this.switch.removeAttribute('aria-disabled');
      }
      this.classList.toggle('is-on', this.on);
      if (this.body) this.body.hidden = !this.on;
      const unavailable = this.querySelector('[data-v2-subs-unavailable]');
      if (unavailable) unavailable.hidden = !variant || available > 0;

      if (this.input) {
        this.input.disabled = !(this.on && plan);
        this.input.value = this.on && plan ? String(plan.id) : '';
      }

      this.#renderText(plan, variant);

      if (userChange && this.input) {
        // ProductForm re-reads the plan price for its sticky bar on form change.
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (announce) this.#announce();
      if (emit) this.emit();
    }

    emit({ initial = false } = {}) {
      document.dispatchEvent(new CustomEvent('v2:subscription-change', { detail: { ...this.detail, initial } }));
    }

    #currentPlan() {
      const radio = this.selectedRadio;
      return radio ? this.plans.get(radio.value) || null : null;
    }

    #saving(plan, variant) {
      if (!plan) return '';
      if (plan.type === 'percentage' && plan.value > 0) return `${Number(plan.value)}%`;
      if (plan.type === 'fixed_amount' && plan.value > 0) return this.#money(plan.value);
      if (plan.type === 'price' && variant) {
        const allocation = variant.plans[plan.id];
        if (allocation && allocation[1] > allocation[0]) return this.#money(allocation[1] - allocation[2]);
      }
      return '';
    }

    #renderText(plan, variant) {
      const d = this.dataset;
      const saving = this.#saving(plan, variant);
      const title = this.querySelector('[data-v2-subs-title]');
      if (title) title.textContent = saving ? fill(d.heading, { saving }) : d.headingPlain;

      let summary;
      const allocation = plan && variant ? variant.plans[plan.id] : null;
      if (this.on && plan && allocation) {
        const [perDelivery, compareAt] = allocation;
        summary = compareAt > perDelivery
          ? fill(d.summaryOn, { price: this.#money(perDelivery), compare_at_price: this.#money(compareAt), rhythm: plan.rhythm })
          : fill(d.summaryOnPlain, { price: this.#money(perDelivery), rhythm: plan.rhythm });
      } else if (saving && plan?.every) {
        summary = fill(d.summaryOff, { saving, rhythms: d.rhythms });
      } else {
        summary = fill(d.summaryOffPlain, { rhythms: d.rhythms });
      }
      const summaryEl = this.querySelector('[data-v2-subs-summary]');
      if (summaryEl) summaryEl.textContent = summary;
      this.summaryText = summary;

      const savingItem = this.querySelector('[data-v2-subs-included-saving]');
      if (savingItem) {
        savingItem.hidden = !(saving && plan?.every);
        const text = savingItem.querySelector('[data-v2-subs-included-saving-text]');
        if (text) text.textContent = saving ? fill(d.includedSaving, { saving }) : '';
      }
      const rhythm = this.querySelector('[data-v2-subs-included-rhythm]');
      if (rhythm && plan) rhythm.textContent = fill(d.planLabel, { rhythm: plan.rhythm });
    }

    /* One polite sentence per shopper change, cleared first so a repeat is read again. */
    #announce() {
      if (!this.status) return;
      const variant = this.variant;
      const text = this.on
        ? fill(this.dataset.statusOn, { summary: this.summaryText })
        : variant ? fill(this.dataset.statusOff, { price: this.#money(variant.price) }) : '';
      this.status.textContent = '';
      cancelAnimationFrame(this.statusFrame);
      this.statusFrame = requestAnimationFrame(() => {
        this.status.textContent = text;
      });
    }

    /* Only the panel's selling_plan travels with the form (Seal's inputs are dropped too), and
       the rhythm radios, which are UI only, are left out. */
    #cleanFormData(formData) {
      formData.delete('selling_plan');
      for (const radio of this.radios) formData.delete(radio.name);
      if (this.input && !this.input.disabled && this.input.value) formData.set('selling_plan', this.input.value);
    }

    /* Seal safety net: hide any Seal widget Seal inserts into this product form, now or later. */
    #watchSeal(signal) {
      const sweep = () => {
        for (const el of this.scope.querySelectorAll(SEAL_SELECTOR)) {
          if (this.contains(el) || el.hasAttribute('data-v2-seal-hidden')) continue;
          el.setAttribute('data-v2-seal-hidden', '');
          el.hidden = true;
          el.inert = true;
          el.setAttribute('aria-hidden', 'true');
        }
        for (const field of this.scope.querySelectorAll('[data-v2-seal-hidden] :is(input, select, textarea, button)')) {
          if (!field.disabled) field.disabled = true;
        }
        // Any other selling_plan field in the form (Seal may add one outside its container).
        if (this.form) {
          for (const field of this.form.querySelectorAll('[name="selling_plan"]')) {
            if (field !== this.input && !field.disabled) field.disabled = true;
          }
        }
        this.#adoptDetailsLink();
      };
      sweep();
      let frame = 0;
      this.sealObserver?.disconnect();
      this.sealObserver = new MutationObserver(() => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(sweep);
      });
      this.sealObserver.observe(this.scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
      signal.addEventListener('abort', () => this.sealObserver?.disconnect());
    }

    /* Without a details_url, reuse the destination of Seal's own Subscription details link. */
    #adoptDetailsLink() {
      const link = this.detailsLink;
      if (!link || !link.hidden) return;
      const sealLink = this.scope.querySelector(`[data-v2-seal-hidden] ${SEAL_DETAILS_LINK}`);
      const href = sealLink?.getAttribute('href') || '';
      if (!href || href === '#' || /^\s*javascript:/i.test(href)) return;
      link.href = sealLink.href;
      if (sealLink.target === '_blank') {
        link.target = '_blank';
        link.rel = 'noopener';
      }
      link.hidden = false;
    }

    #money(cents) {
      const format = this.dataset.moneyFormat || '${{amount}}';
      const value = Number(cents) / 100;
      const group = (number, decimals, thousands, decimal) => {
        const [whole, fraction] = number.toFixed(decimals).split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction ? `${grouped}${decimal}${fraction}` : grouped;
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        switch (key) {
          case 'amount_no_decimals': return group(value, 0, ',', '.');
          case 'amount_with_comma_separator': return group(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return group(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return group(value, 2, "'", '.');
          default: return group(value, 2, ',', '.');
        }
      });
    }
  }

  customElements.define('v2-subscription-panel', V2SubscriptionPanel);
})();
